// The manuals offline pack on the Manuals page (src/lib/manualOffline.js): what is on this device for an emergency with no
// internet - contingency plans' manuals, SMS emergency sections, LSA / FFE manuals (current approved version only).
import { useEffect, useState } from 'react'
import { useAuth, rightsHash } from '../hooks/useAuth'
import { readPack, refreshOfflinePack } from '../lib/manualOffline'
import { openManualFile } from '../lib/manualFiles'

const mb = n => (n / 1024 / 1024).toFixed(n > 10 * 1024 * 1024 ? 0 : 1) + ' MB'
const when = t => new Date(t).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

export default function ManualsOfflinePack() {
  const { user, config, rawConfig } = useAuth()
  const uid = user && user.id
  const [pack, setPack] = useState(null)
  const [online, setOnline] = useState(typeof navigator === 'undefined' || navigator.onLine !== false)
  const [open, setOpen] = useState(!online)
  const [busy, setBusy] = useState(false)
  const [read, setRead] = useState(null)   // manual id whose sections are shown
  // the pack as it is now, then the (6-hourly, shared) refresh the login started - so a pack still being built shows up
  useEffect(() => {
    if (!uid || !config) return
    let live = true
    readPack(uid).then(p => { if (live) setPack(p) })
    refreshOfflinePack({ uid, clientId: config.id, rightsHash: rightsHash(rawConfig || config) }).then(r => { if (live && r && r.pack) setPack(r.pack) }, () => {})
    return () => { live = false }
  }, [uid, config && config.id]) // eslint-disable-line
  useEffect(() => {
    const up = () => setOnline(true), down = () => { setOnline(false); setOpen(true) }
    window.addEventListener('online', up); window.addEventListener('offline', down)
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down) }
  }, [])
  if (!uid || !config) return null
  const items = (pack && pack.vessel === config.id && pack.items) || []
  async function update() {
    setBusy(true)
    const r = await refreshOfflinePack({ uid, clientId: config.id, rightsHash: rightsHash(rawConfig || config), force: true }).catch(() => null)
    setPack((r && r.pack) || await readPack(uid)); setBusy(false); setOpen(true)
  }
  const files = items.filter(x => x.cached).length, texts = items.filter(x => x.sections && x.sections.length).length
  return (
    <div style={{ border: '1px solid ' + (online ? 'var(--line)' : 'var(--amber, #8a5c0a)'), borderRadius: 10, padding: '10px 14px', marginBottom: 14, background: online ? 'var(--card)' : 'var(--amber-soft, rgba(185,125,20,.1))' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12.5 }}>
        <b>Offline pack</b>
        <span style={{ color: 'var(--muted)' }}>{!online ? 'You are offline - these manuals are on this device. ' : ''}{items.length ? items.length + ' manual' + (items.length === 1 ? '' : 's') + ' (' + files + ' file' + (files === 1 ? '' : 's') + (pack && pack.bytes ? ', ' + mb(pack.bytes) : '') + (texts ? ', ' + texts + ' with emergency sections' : '') + ') · updated ' + when(pack.at) : 'Nothing on this device yet.'}</span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {items.length > 0 && <button type="button" className="btn" style={{ padding: '3px 10px', fontSize: 12 }} aria-expanded={open} onClick={() => setOpen(o => !o)}>{open ? 'Hide' : 'Show'}</button>}
          {online && <button type="button" className="btn" style={{ padding: '3px 10px', fontSize: 12 }} disabled={busy} onClick={update}>{busy ? 'Updating…' : 'Update now'}</button>}
        </span>
      </div>
      {open && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6 }}>Kept for emergencies with no internet: the manuals linked to a Contingency Plan, the SMS emergency sections and the LSA / FFE manuals - the current approved version only.</div>}
      {open && items.map(x => (
        <div key={x.id} style={{ borderTop: '1px solid var(--line)', padding: '6px 0', fontSize: 13 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ flex: 1, minWidth: 180 }}>{x.title} <span style={{ color: 'var(--muted)', fontSize: 11.5 }}>v{x.version} · {x.reasons.join(' · ')}{x.error ? ' · ' + x.error : ''}</span></span>
            {x.cached && x.file_url && <button type="button" className="btn" style={{ padding: '2px 9px', fontSize: 12 }} onClick={() => openManualFile(x.file_url, x.id)}>Open</button>}
            {x.sections && x.sections.length > 0 && <button type="button" className="btn" style={{ padding: '2px 9px', fontSize: 12 }} aria-expanded={read === x.id} onClick={() => setRead(r => (r === x.id ? null : x.id))}>{read === x.id ? 'Close' : 'Read'}</button>}
          </div>
          {read === x.id && (x.sections || []).map((sec, i) => (
            <div key={i} style={{ margin: '8px 0 4px', padding: '8px 10px', borderRadius: 8, background: 'var(--bg2, var(--card))' }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{sec.heading}</div>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 12.5, lineHeight: 1.55, marginTop: 4 }}>{sec.body}</div>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}
